import React from 'react';
import PropTypes from 'prop-types';
import Moment from 'moment';

import { Colors, FontSizes } from 'constants/Clementine';

import Select from 'shared/Select';

class DatePicker extends React.Component {
  static propTypes = {
    hidePlaceholders: PropTypes.bool,
    id: PropTypes.string,
    invalid: PropTypes.bool,
    onChange: PropTypes.func,
    readOnly: PropTypes.bool,
    required: PropTypes.bool,
    value: PropTypes.string
  };

  constructor(props) {
    super(props);

    let value_m = null;

    if (props.value && props.value !== 'Invalid date') {
      //  create a moment object from the string to preserve the exact date
      value_m = Moment(props.value, 'YYYY-MM-DD');

      if (!value_m.isValid()) {
        // Fallback to Date object if the direct parsing fails
        const date = new Date(props.value);
        if (!isNaN(date)) {
          value_m = Moment(date);
        } else {
          console.warn('Date is not in a recognized RFC2822 or ISO format');
        }
      }
    }

    this.state = {
      month: value_m && value_m.isValid() ? value_m.format('MM') : '',
      day: value_m && value_m.isValid() ? value_m.format('DD') : '',
      year: value_m && value_m.isValid() ? value_m.format('YYYY') : '',
      initialValue: props.value // Store the initial value to detect external changes
    };
  }

  // Add support for external value updates
  componentDidUpdate(prevProps) {
    // Check if the value prop changed from outside
    if (prevProps.value !== this.props.value && this.props.value !== this.state.initialValue) {
      let value_m = null;

      if (this.props.value && this.props.value !== 'Invalid date') {
        // Directly create a moment object from the string to preserve the exact date
        value_m = Moment(this.props.value, 'YYYY-MM-DD');

        if (!value_m.isValid()) {
          // Fallback to Date object if the direct parsing fails
          const date = new Date(this.props.value);
          if (!isNaN(date)) {
            value_m = Moment(date);
          }
        }
      }

      if (value_m && value_m.isValid()) {
        this.setState({
          month: value_m.format('MM'),
          day: value_m.format('DD'),
          year: value_m.format('YYYY'),
          initialValue: this.props.value
        });
      }
    }
  }

  _generateMonths = () => {
    const options = this.props.hidePlaceholders ? [] : [{ value: '', label: '--' }];

    return options.concat([
      { value: '01', label: 'January' },
      { value: '02', label: 'February' },
      { value: '03', label: 'March' },
      { value: '04', label: 'April' },
      { value: '05', label: 'May' },
      { value: '06', label: 'June' },
      { value: '07', label: 'July' },
      { value: '08', label: 'August' },
      { value: '09', label: 'September' },
      { value: '10', label: 'October' },
      { value: '11', label: 'November' },
      { value: '12', label: 'December' }
    ]);
  };

  _generateDays = () => {
    const days = [];
    const year = this.state.year || '3000';
    const num_days = this.state.month ? Moment(`${year}-${this.state.month}-01`).daysInMonth() : 31;
    const options = this.props.hidePlaceholders ? [] : [{ value: '', label: '--' }];

    for (let i = 1; i <= num_days; i++) {
      days.push(i);
    }

    return options.concat(
      days.map(d => {
        return {
          value: d < 10 ? `0${d}` : `${d}`,
          label: d
        };
      })
    );
  };

  _generateYears = () => {
    const years = [];
    const this_year = parseInt(Moment().format('YYYY'), 10);
    const options = this.props.hidePlaceholders ? [] : [{ value: '', label: '--' }];

    for (let i = this_year - 120; i <= this_year; i++) {
      years.push(i);
    }

    return options.concat(years.map(y => ({ value: `${y}` })));
  };

  _handleMonthChange = e => {
    const newMonth = e.target.value;
    const currentYear = this.state.year || '';
    const currentDay = this.state.day || '';

    if (!newMonth) {
      this.setState({ month: '' }, () => {
        this._notifyDateChange();
      });
      return;
    }

    // Check if we have a valid date to work with
    if (!currentYear) {
      // Just set the month if we don't have a year yet
      this.setState({ month: newMonth }, () => {
        this._notifyDateChange();
      });
      return;
    }

    // Calculate max days for the new month/year combination
    const maxDays = Moment(`${currentYear}-${newMonth}-01`).daysInMonth();
    let adjustedDay = currentDay;

    // Adjust the day if needed
    if (currentDay && parseInt(currentDay, 10) > maxDays) {
      adjustedDay = maxDays < 10 ? `0${maxDays}` : `${maxDays}`;
    }

    this.setState({ month: newMonth, day: adjustedDay }, () => {
      this._notifyDateChange();
    });
  };

  _handleDayChange = e => {
    const newDay = e.target.value;

    this.setState({ day: newDay }, () => {
      this._notifyDateChange();
    });
  };

  _handleYearChange = e => {
    const newYear = e.target.value;
    const currentMonth = this.state.month || '';
    const currentDay = this.state.day || '';

    // If we have month and day, check if the day is valid for the new year
    if (newYear && currentMonth && currentDay) {
      const maxDays = Moment(`${newYear}-${currentMonth}-01`).daysInMonth();
      let adjustedDay = currentDay;

      if (parseInt(currentDay, 10) > maxDays) {
        adjustedDay = maxDays < 10 ? `0${maxDays}` : `${maxDays}`;
        this.setState({ year: newYear, day: adjustedDay }, () => {
          this._notifyDateChange();
        });
        return;
      }
    }

    this.setState({ year: newYear }, () => {
      this._notifyDateChange();
    });
  };

  // notify parent of date changes
  _notifyDateChange = () => {
    const { year, month, day } = this.state;

    if (year && month && day) {
      // Format the exact string without any conversions
      const dateString = `${year}-${month}-${day}`;

      // Store this as the new initial value to prevent loops
      this.setState({ initialValue: dateString });

      // Notify the parent component
      if (this.props.onChange) {
        this.props.onChange(dateString);
      }
    } else if (this.props.onChange) {
      // If any part is missing, pass empty string
      this.props.onChange('');
    }
  };

  render() {
    const { readOnly, id, required } = this.props;
    const styles = this.styles();

    return (
      <div id={id} style={styles.component}>
        <Select
          id='date-picker-month'
          invalid={this.props.invalid}
          onChange={this._handleMonthChange}
          options={this._generateMonths()}
          readOnly={readOnly}
          style={{ marginRight: -1, borderRadius: '4px 0px 0px 4px', minWidth: 110 }}
          value={this.state.month}
        />
        <Select
          id='date-picker-days'
          invalid={this.props.invalid}
          onChange={this._handleDayChange}
          options={this._generateDays()}
          readOnly={readOnly}
          style={{ marginRight: -1, borderRadius: 0, minWidth: 50 }}
          value={this.state.day}
        />
        <Select
          id='date-picker-year'
          invalid={this.props.invalid}
          onChange={this._handleYearChange}
          options={this._generateYears()}
          readOnly={readOnly}
          style={{ marginRight: -1, borderRadius: '0px 4px 4px 0px', minWidth: 75 }}
          value={this.state.year}
        />
        {required ? <span style={styles.required}>*</span> : null}
      </div>
    );
  }

  styles = () => {
    return {
      component: Object.assign(
        {
          display: 'flex',
          position: 'relative'
        },
        this.props.style
      ),
      required: {
        color: Colors.RED.hex,
        position: 'absolute',
        right: 5,
        top: 5,
        lineHeight: 1,
        fontSize: FontSizes.LARGE
      }
    };
  };
}

export default DatePicker;
